import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import PageLayout from "@/components/feature/PageLayout";
import MovieCard from "@/components/feature/MovieCard";
import { useSeo } from "@/hooks/useSeo";
import { useFavorites } from "@/hooks/useFavorites";
import {
  allMovies,
  filterMovies,
  getGenre,
  getGenreLabel,
  sortMovies,
  type SortKey,
} from "@/lib/movie";
import FilterBar from "./components/FilterBar";

const PAGE_SIZE = 8;

export default function Category() {
  const { type } = useParams<{ type: string }>();
  const genre = getGenre(type);
  const label = genre?.label ?? "全部影片";

  const [selectedGenres, setSelectedGenres] = useState<string[]>([]);
  const [year, setYear] = useState<number | "all">("all");
  const [minRating, setMinRating] = useState(0);
  const [sort, setSort] = useState<SortKey>("release");
  const [visible, setVisible] = useState(PAGE_SIZE);
  const { isFavorite, toggleFavorite } = useFavorites();

  useEffect(() => {
    setSelectedGenres(type ? [type] : []);
    setYear("all");
    setMinRating(0);
    setSort("release");
    setVisible(PAGE_SIZE);
  }, [type]);

  const results = useMemo(() => {
    const filtered = filterMovies(allMovies, { genres: selectedGenres, year, minRating });
    return sortMovies(filtered, sort);
  }, [selectedGenres, year, minRating, sort]);

  const shown = results.slice(0, visible);

  const toggleGenre = (slug: string) => {
    setSelectedGenres((prev) =>
      prev.includes(slug) ? prev.filter((s) => s !== slug) : [...prev, slug]
    );
    setVisible(PAGE_SIZE);
  };

  const reset = () => {
    setSelectedGenres(type ? [type] : []);
    setYear("all");
    setMinRating(0);
    setSort("release");
    setVisible(PAGE_SIZE);
  };

  useSeo({
    title: `${label}电影 - 最新电影`,
    description: `${label}电影专题页，汇集最新${label}影片的上映时间、评分介绍与剧情简介，支持按年份与评分区间筛选，快速找到值得一看的${label}佳作。`,
    keywords: `${label}电影,${label}片推荐,最新${label}电影,${label}电影评分,电影资讯`,
    canonicalPath: `/category/${type ?? ""}`,
  });

  return (
    <PageLayout>
      <div className="w-full bg-primary-950">
        <div className="w-full max-w-[1360px] mx-auto px-4 md:px-6 py-10 md:py-12">
          <nav className="flex items-center gap-2 text-xs text-background-400 mb-4">
            <Link to="/" className="hover:text-background-100 transition-colors cursor-pointer">
              首页
            </Link>
            <i className="ri-arrow-right-s-line"></i>
            <span>分类</span>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-background-100">{label}</span>
          </nav>
          <div className="flex items-center gap-3">
            <span className="w-12 h-12 flex items-center justify-center rounded-lg bg-accent-500 text-background-50">
              <i className={`${genre?.icon ?? "ri-movie-2-line"} text-2xl`}></i>
            </span>
            <div>
              <h1 className="text-2xl md:text-3xl font-heading font-black text-background-50">
                {label}电影
              </h1>
              <p className="text-sm text-background-300 mt-1">
                {genre?.desc ?? "浏览全站收录的最新影片"}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="w-full max-w-[1360px] mx-auto px-4 md:px-6 py-8 md:py-10">
        <FilterBar
          selectedGenres={selectedGenres}
          onToggleGenre={toggleGenre}
          year={year}
          onYearChange={(v) => {
            setYear(v);
            setVisible(PAGE_SIZE);
          }}
          minRating={minRating}
          onRatingChange={(v) => {
            setMinRating(v);
            setVisible(PAGE_SIZE);
          }}
          sort={sort}
          onSortChange={setSort}
          onReset={reset}
        />

        <div className="flex items-center justify-between mt-6 mb-4">
          <p className="text-sm text-foreground-600">
            共找到 <strong className="text-accent-600">{results.length}</strong> 部符合条件的电影
          </p>
        </div>

        {shown.length === 0 ? (
          <div className="rounded-lg border border-dashed border-background-300 bg-background-100 py-20 flex flex-col items-center text-center">
            <span className="w-14 h-14 flex items-center justify-center rounded-full bg-background-200 text-foreground-400">
              <i className="ri-emotion-sad-line text-2xl"></i>
            </span>
            <h3 className="mt-4 text-foreground-900 font-semibold">暂无符合条件的电影</h3>
            <p className="mt-1.5 text-sm text-foreground-500">试着放宽筛选条件，也许会有更多发现</p>
            <button
              type="button"
              onClick={reset}
              className="mt-5 inline-flex items-center gap-1.5 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-refresh-line"></i>
              重置筛选条件
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
            {shown.map((movie) => (
              <MovieCard
                key={movie.id}
                movie={movie}
                isFavorite={isFavorite(movie.id)}
                onToggleFavorite={toggleFavorite}
              />
            ))}
          </div>
        )}

        {shown.length > 0 && visible < results.length && (
          <div className="flex justify-center mt-10">
            <button
              type="button"
              onClick={() => setVisible((v) => v + PAGE_SIZE)}
              className="inline-flex items-center gap-2 px-7 py-3 rounded-md border border-primary-300 text-primary-700 text-sm font-medium hover:bg-primary-50 transition-colors cursor-pointer whitespace-nowrap"
            >
              加载更多
              <i className="ri-arrow-down-line"></i>
            </button>
          </div>
        )}

        <div className="mt-12 pt-8 border-t border-background-200">
          <p className="text-sm font-semibold text-foreground-700 mb-3">浏览其他类型</p>
          <div className="flex flex-wrap gap-2">
            {["action", "romance", "comedy", "sci-fi", "horror", "animation", "mystery", "drama", "adventure"].map(
              (slug) => (
                <Link
                  key={slug}
                  to={`/category/${slug}`}
                  className={`px-3.5 py-1.5 rounded-full text-sm transition-colors cursor-pointer whitespace-nowrap ${
                    slug === type
                      ? "bg-accent-500 text-background-50"
                      : "bg-background-100 text-foreground-700 hover:bg-accent-100 hover:text-accent-700"
                  }`}
                >
                  {getGenreLabel(slug)}
                </Link>
              )
            )}
          </div>
        </div>
      </div>
    </PageLayout>
  );
}